// vet-summary.ts — the record, written out so it can be handed to a vet.
//
// The app's pitch is "the page the vet asks you about". That only pays off if the owner can
// actually hand it over — a CSV of moments does not do that. This produces a plain, printable
// summary of ONE pet's care record: the weight history, what was done and when, what was noticed,
// and what is due next.
//
// Deliberately FACTUAL and un-interpreted: it reports the owner's own entries and computes nothing
// beyond dates and arithmetic. No assessment, no "possible causes", no advice — the app is not a
// vet, and a document a real clinician reads is exactly the wrong place to start guessing. Built
// on-device from local rows; nothing is sent anywhere.

import type { Care, Pet } from "./store";
import { buildWeightTrend } from "./store";

function pretty(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso;
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${d} ${months[m - 1]} ${y}`;
}
const fmtKg = (n: number) => String(Math.round(n * 100) / 100);

/** Whole years between a date and today; null when the date is missing/unparseable. */
function ageYears(birth: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}/.test(birth)) return null;
  const b = new Date(birth.slice(0, 10) + "T00:00:00");
  const now = new Date();
  let y = now.getFullYear() - b.getFullYear();
  const before = now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate());
  if (before) y -= 1;
  return y >= 0 ? y : null;
}

function section(title: string, lines: string[]): string {
  if (!lines.length) return "";
  return `${title}\n${"-".repeat(title.length)}\n${lines.join("\n")}\n\n`;
}

export function buildVetSummary(pet: Pet | null, care: Care[], today: string): string {
  const name = pet?.name?.trim() || "This pet";
  const kind = pet?.kind?.trim();
  const age = pet?.birth_on ? ageYears(pet.birth_on) : null;

  const head: string[] = [];
  if (kind) head.push(`Species/breed as recorded: ${kind}`);
  if (pet?.birth_on) head.push(`Date of birth / gotcha day: ${pretty(pet.birth_on)}${age !== null ? ` (about ${age} ${age === 1 ? "year" : "years"} old)` : ""}`);
  if (pet?.farewell_on) head.push(`Died: ${pretty(pet.farewell_on)}`);

  const byKind = (k: string) => care.filter((c) => c.kind === k);
  const trend = buildWeightTrend(care);

  // Weight — oldest first, because a clinician reads a trend forwards.
  const weights = trend.points.map((p) => `  ${pretty(p.on)} — ${fmtKg(p.kg)} kg`);
  if (trend.changeKg !== null && trend.points.length >= 2) {
    const d = trend.changeKg;
    weights.push(
      Math.abs(d) < 0.05
        ? "  Net change across the record: none"
        : `  Net change across the record: ${d > 0 ? "+" : "-"}${fmtKg(Math.abs(d))} kg`,
    );
  }

  const visits = byKind("vet").map((c) =>
    `  ${pretty(c.happened_on)} — ${c.title || "Vet visit"}${c.detail ? `\n      ${c.detail}` : ""}`);
  const meds = byKind("med").map((c) =>
    `  ${pretty(c.happened_on)} — ${c.title || "Medication"}${c.detail ? `\n      ${c.detail}` : ""}`);
  const symptoms = byKind("symptom").map((c) =>
    `  ${pretty(c.happened_on)} — ${c.title || "Noted"}${c.detail ? `\n      ${c.detail}` : ""}`);
  const notes = byKind("note").map((c) =>
    `  ${pretty(c.happened_on)} — ${c.title || "Note"}${c.detail ? `\n      ${c.detail}` : ""}`);

  const due = care
    .filter((c) => /^\d{4}-\d{2}-\d{2}$/.test(c.due_on || "") && c.due_on >= today)
    .sort((a, b) => a.due_on.localeCompare(b.due_on))
    .map((c) => `  ${pretty(c.due_on)} — ${c.title || c.kind}`);

  const body =
    section("Weight", weights) +
    section("Symptoms noticed", symptoms) +
    section("Vet visits", visits) +
    section("Medication", meds) +
    section("Other notes", notes) +
    section("Due next", due);

  return (
    `CARE RECORD — ${name}\n` +
    `${"=".repeat(`CARE RECORD — ${name}`.length)}\n` +
    (head.length ? head.join("\n") + "\n" : "") +
    `Prepared ${pretty(today)} from the owner's own records.\n\n` +
    (body || "No entries recorded yet.\n\n") +
    `This is a record kept by the owner in the Forever Paw app. It reports what they entered and\n` +
    `nothing more — it contains no assessment, diagnosis or advice.\n`
  );
}

export function vetSummaryFilename(pet: Pet | null, today: string): string {
  const slug = (pet?.name?.trim() || "pet").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `${slug}-care-record-${today}.txt`;
}
